import { Resources, type useResource } from "Infrastructure/Translations/Resources";
import { getCzechPluralForm, inflectMonths, inflectYears } from "Infrastructure/Utils/LanguageUtils";
import { differenceInCalendarMonths, differenceInYears, parseISO } from "date-fns";

export type Translate = ReturnType<typeof useResource>["t"];

const RelativeTime = Resources.AnalysisDetail.Goals.Timeline.RelativeTime;

/** `parseISO` reads a date-only string as a local date, `new Date` would take it as UTC midnight. */
export const toDate = (value: string | Date) => (typeof value === "string" ? parseISO(value) : value);

export const formatMonthYear = (value: string | Date) => {
  const date = toDate(value);
  return `${date.getMonth() + 1}/${date.getFullYear()}`;
};

export const getClientAge = (birthDate: string, date: string | Date) => differenceInYears(toDate(date), toDate(birthDate));

const inflectMonthsAgo = (count: number, t: Translate) =>
  t(getCzechPluralForm(count, { one: RelativeTime.MonthsAgo_one, few: RelativeTime.MonthsAgo_few, other: RelativeTime.MonthsAgo_other }), {
    count,
  });

const inflectYearsAgo = (count: number, t: Translate) =>
  t(getCzechPluralForm(count, { one: RelativeTime.YearsAgo_one, few: RelativeTime.YearsAgo_few, other: RelativeTime.YearsAgo_other }), {
    count,
  });

const formatFutureDuration = (months: number, t: Translate) => {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) return inflectMonths(remainingMonths, t);
  if (remainingMonths === 0) return inflectYears(years, t);
  return t(RelativeTime.YearsAndMonths, { years: inflectYears(years, t), months: inflectMonths(remainingMonths, t) });
};

// Past dates a year or more back are given in whole years only ("před 3 lety").
const formatPastDuration = (months: number, t: Translate) =>
  months < 12 ? inflectMonthsAgo(months, t) : inflectYearsAgo(Math.floor(months / 12), t);

/** Distance from the current month in whole calendar months, e.g. "tento měsíc", "za 3 roky a 7 měsíců", "před 2 měsíci". */
export const formatRelativeTime = (date: string | Date, now: Date, t: Translate) => {
  const months = differenceInCalendarMonths(toDate(date), now);

  if (months === 0) return t(RelativeTime.ThisMonth);
  if (months > 0) return t(RelativeTime.Future, { duration: formatFutureDuration(months, t) });
  return t(RelativeTime.Past, { duration: formatPastDuration(-months, t) });
};
